import React, { useState, useMemo } from'react';
import { useSearchParams } from'react-router-dom';
import { useLayout } from'../components/Layout';
import { useApiQuery } from'../hooks/useApi';
import api from'../api/client';
import DataTable from'../components/tables/DataTable';
import AreaChart from'../components/charts/AreaChart';
import BarChart from'../components/charts/BarChart';
import HeatmapChart from'../components/charts/HeatmapChart';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import PageLoader, { InlineLoader } from'../components/PageLoader';

// Maps the `?sort=` deep-link token to a table column + direction. Keeps the
// URL contract readable ("/apis?sort=errorRate") and decoupled from the
// internal field names.
const SORT_PRESETS = {
 errorRate: { key:'errorRate', dir:'desc' },
 calls: { key:'totalCalls', dir:'desc' },
 latency: { key:'p95Latency', dir:'desc' },
 rejected: { key:'edgeRejections', dir:'desc' },
};

export default function ApiAnalytics() {
 const { timeRange, granularity } = useLayout();
 const [selectedApi, setSelectedApi] = useState(null);
 const [searchParams] = useSearchParams();

 // Deep-link support: the Dashboard KPI cards navigate here with a sort
 // (and optionally a filter) so the user lands on the rows they clicked
 // to investigate, rather than an unordered list they must re-sort.
 const sortParam = searchParams.get('sort');
 const statusParam = searchParams.get('status'); //'failing' — APIs with errors;'rejected' — APIs with edge rejections
 // When deep-linked with ?status=rejected (from the Edge Rejections KPI) but
 // no explicit ?sort, default to sorting by edge rejections so the offending
 // APIs surface at the top.
 const initialSort = SORT_PRESETS[sortParam]
 || (statusParam ==='rejected' ? SORT_PRESETS.rejected : null);

 const { data: apis, isPending: apisPending } = useApiQuery(
 ['apis', timeRange],
 () => api.getApis(timeRange),
 { refetchInterval: 60000 }
 );

 const { data: timeSeries, isPending: timeSeriesPending } = useApiQuery(
 ['api-timeseries', selectedApi, timeRange, granularity],
 () => api.getApiTimeSeries(selectedApi, timeRange, granularity),
 { enabled: !!selectedApi, refetchInterval: 30000 }
 );

 const { data: heatmap } = useApiQuery(
 ['api-heatmap', selectedApi],
 () => api.getApiHeatmap(selectedApi,'7d'),
 { enabled: !!selectedApi }
 );

 const { data: errors } = useApiQuery(
 ['api-errors', selectedApi, timeRange],
 () => api.getApiErrors(selectedApi, timeRange),
 { enabled: !!selectedApi }
 );

 // When deep-linked with `?status=failing` (from the Error Rate KPI) show
 // only APIs that actually have errors, so the user isn't scanning past
 // healthy rows to find the problem.
 const visibleApis = useMemo(() => {
 const all = apis || [];
 if (statusParam ==='failing') {
 return all.filter((a) => (a.errorCount || 0) > 0);
 }
 if (statusParam ==='rejected') {
 return all.filter((a) => (a.edgeRejections || 0) > 0);
 }
 return all;
 }, [apis, statusParam]);

 // Lightweight reconciling totals so this screen ties out to the Executive
 // Dashboard at a glance (served Calls sum, total Errors, total Edge
 // Rejections). Computed over the CURRENTLY VISIBLE rows so the strip always
 // describes what the table shows.
 const totals = useMemo(() => {
 return (visibleApis || []).reduce(
 (acc, a) => {
 acc.calls += a.totalCalls || 0;
 acc.errors += a.errorCount || 0;
 acc.rejections += a.edgeRejections || 0;
 return acc;
 },
 { calls: 0, errors: 0, rejections: 0 }
 );
 }, [visibleApis]);

 const columns = [
 { headerName:'API', field:'displayName', flex: 2,
 cellRenderer: (p) => (
 <span className="font-medium text-brand-600 cursor-pointer hover:underline">
 {p.value}
 </span>
 ),
 },
 { headerName:'Path', field:'path', flex: 1 },
 { headerName:'Calls', field:'totalCalls', width: 100, type:'numericColumn',
 valueFormatter: (p) => (p.value || 0).toLocaleString() },
 { headerName:'Avg Latency', field:'avgLatency', width: 110, type:'numericColumn',
 valueFormatter: (p) =>`${p.value || 0}ms` },
 { headerName:'P95', field:'p95Latency', width: 90, type:'numericColumn',
 valueFormatter: (p) =>`${p.value || 0}ms` },  { headerName:'Errors', field:'errorCount', width: 80, type:'numericColumn' },
 { headerName:'Error %', field:'errorRate', width: 90, type:'numericColumn',
 valueFormatter: (p) =>`${(p.value || 0).toFixed(1)}%`,
 cellStyle: (p) => p.value > 5 ? { color:'#ef4444' } : {},
 },
 { headerName:'Edge Rejections', field:'edgeRejections', width: 130, type:'numericColumn',
 headerTooltip:'Requests refused at the gateway (401/403/429 — bad token, forbidden, or rate-limited). Not counted as calls or errors.',
 valueFormatter: (p) => (p.value || 0).toLocaleString(),
 cellStyle: (p) => p.value > 0 ? { color:'#d97706' } : {},
 },
 ];

 return (
 <div className="space-y-6">
 <Breadcrumbs />      <div>
 <h1 className="text-xl font-bold">API Analytics</h1>
 <p className="text-sm text-gray-500 mt-0.5">
 Per-API performance metrics and usage analysis
 </p>
 {statusParam ==='failing' && (
 <p className="text-sm text-red-600 mt-1">
 Showing only APIs with errors — sorted by error rate.
 </p>
 )}
 {statusParam ==='rejected' && (
 <p className="text-sm text-amber-600 mt-1">
 Showing only APIs with edge rejections (401/403/429) — sorted by rejection count.
 </p>
 )}
 </div>

 {/* Reconciling summary strip — ties out to the Executive Dashboard:
     served Calls, failed calls (Errors), and Edge Rejections use the
     same served-traffic definitions as the dashboard headline. */}
 {!apisPending && (
 <div className="grid grid-cols-3 gap-3 sm:gap-4">
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Served Calls
 </div>
 <div className="mt-1 text-lg font-bold tabular-nums text-gray-900">
 {totals.calls.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">across {visibleApis.length} APIs</div>
 </div>
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Errors
 </div>
 <div className={`mt-1 text-lg font-bold tabular-nums ${totals.errors > 0 ?'text-red-600' :'text-gray-900'}`}>
 {totals.errors.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">
 {totals.calls > 0 ? `${((totals.errors / totals.calls) * 100).toFixed(1)}% of served` :'—'}
 </div>
 </div>
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Edge Rejections
 </div>
 <div className={`mt-1 text-lg font-bold tabular-nums ${totals.rejections > 0 ?'text-amber-600' :'text-gray-900'}`}>
 {totals.rejections.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">blocked at gateway (401/403/429)</div>
 </div>
 </div>
 )}

 <ErrorBoundary>
 {apisPending ? (
 <PageLoader message="Loading APIs…" />
 ) : (
 <DataTable
 data={visibleApis}
 columns={columns}
 title={`${visibleApis.length} APIs`}
 height={350}
 initialSort={initialSort}
 exportFilename="api-analytics"
 onRowClick={(row) => setSelectedApi(row.id || row.displayName)}
 />
 )}
 </ErrorBoundary>

 {selectedApi && (
 <>
 <div className="pt-2 border-t border-gray-200">
 <h2 className="text-lg font-semibold">{selectedApi}</h2>
 </div>

 {timeSeriesPending ? (
 <InlineLoader message="Loading API analytics…" />
 ) : (
 <>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ErrorBoundary>
 <AreaChart
 data={timeSeries || []}
 dataKeys={['Calls','Errors']}
 title="Calls & Errors Over Time"
 exportFilename={`api-${selectedApi}-calls`}
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <AreaChart
 data={timeSeries || []}
 dataKeys={['AvgLatency','P95Latency']}
 title="Latency Over Time (ms)"
 exportFilename={`api-${selectedApi}-latency`}
 />
 </ErrorBoundary>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ErrorBoundary>
 <HeatmapChart
 data={heatmap || []}
 title="Usage Heatmap (Last 7 Days)"
 />
 </ErrorBoundary>
 {errors && errors.length > 0 && (
 <ErrorBoundary>
 <BarChart                data={errors.map((e) => ({ name:`HTTP ${e.ResponseCode}`, Count: parseInt(e.Count, 10) }))}
 dataKeys={['Count']}
 xAxisKey="name"
 title="Error Breakdown"
 colorByIndex
 height={250}
 exportFilename={`api-${selectedApi}-errors`}
 />
 </ErrorBoundary>
 )}
 </div>
 </>
 )}
 </>
 )}
 </div>
 );
}
